'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Moon, Plus, Search, Sun } from 'lucide-react';

import type { DashboardTheme } from '@/components/dashboard/useDashboardTheme';
import { initials, roleLabel, type DashboardUser } from '@/components/dashboard/types';

export function ThemeToggle({ theme, onToggle }: { theme: DashboardTheme; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      className="rounded-full border border-line p-2 text-muted transition-colors hover:text-ink"
    >
      {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
    </button>
  );
}

export function Header({
  title,
  user,
  theme,
  onToggleTheme,
  onSearch,
  onSignOut,
  onEditProfile,
  primaryAction,
}: {
  title: string;
  user: DashboardUser;
  theme: DashboardTheme;
  onToggleTheme: () => void;
  onSearch: () => void;
  onSignOut: () => void;
  onEditProfile?: () => void;
  primaryAction?: { href: string; label: string };
}) {
  return (
    <header className="sticky top-9 z-20 flex h-14 items-center justify-between gap-3 border-b border-line bg-shell/90 px-4 backdrop-blur-md sm:px-6">
      <h1 className="type-section truncate text-ink">{title}</h1>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSearch}
          className="inline-flex items-center gap-2 rounded-full border border-line px-3 py-1.5 text-xs text-muted transition-colors hover:text-ink"
        >
          <Search size={13} />
          Search
          <span className="hidden text-muted sm:inline">Ctrl K</span>
        </button>
        <ThemeToggle theme={theme} onToggle={onToggleTheme} />
        {primaryAction && (
          <Link
            href={primaryAction.href}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 transition-colors hover:brightness-110"
          >
            <Plus size={14} />
            {primaryAction.label}
          </Link>
        )}
        <AccountMenu user={user} onSignOut={onSignOut} onEditProfile={onEditProfile} />
      </div>
    </header>
  );
}

export function AccountMenu({ user, onSignOut, onEditProfile }: { user: DashboardUser; onSignOut: () => void; onEditProfile?: () => void }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onPointer(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onPointer);
    return () => window.removeEventListener('mousedown', onPointer);
  }, []);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((current) => !current)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-[11px] font-bold text-slate-950"
      >
        {initials(user.name, user.email)}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-64 rounded-2xl border border-line bg-card p-3 text-ink shadow-2xl"
        >
          <p className="truncate text-sm font-semibold">{user.name || user.email}</p>
          <p className="truncate text-xs text-muted">{user.email}</p>
          <p className="mt-2 inline-flex rounded-full border border-brand/40 bg-brand/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">
            {roleLabel(user.role)}
          </p>
          {onEditProfile && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onEditProfile();
              }}
              className="mt-3 w-full rounded-xl px-3 py-2 text-left text-xs font-semibold hover:bg-shell"
            >
              My profile
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="mt-3 w-full rounded-xl px-3 py-2 text-left text-xs font-semibold hover:bg-shell"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
